import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  ShieldCheck, Database, AlertTriangle, Wrench, Zap, Loader2,
  CheckCircle, RefreshCw, History, Eye, X
} from 'lucide-react';

const DataIntegrity = ({ user }) => {
  const { t } = useTranslation();
  const [scan, setScan] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [issues, setIssues] = useState([]);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [healing, setHealing] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const fetchAll = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([
        axiosInstance.get('/data-integrity/dashboard'),
        axiosInstance.get('/data-integrity/runs?limit=10'),
      ]);
      setDashboard(d.data);
      setRuns(r.data.runs || []);
      if (d.data?.last_run) setScan(d.data.last_run);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleScan = async () => {
    if (!isAdmin) return;
    setRunning(true);
    try {
      const r = await axiosInstance.post('/data-integrity/scan');
      setScan(r.data);
      toast.success(
        r.data.total_issues === 0
          ? `✅ النظام سليم — لا توجد مشاكل / System clean — no issues`
          : `⚠️ تم اكتشاف ${r.data.total_issues} مشكلة / Found ${r.data.total_issues} issues`
      );
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setRunning(false);
    }
  };

  const handleHeal = async (collection, dryRun) => {
    if (!isAdmin) return;
    if (!dryRun && !window.confirm(
      `هل تريد تطبيق الإصلاحات على ${collection}؟ هذا الإجراء غير قابل للتراجع.\n\n` +
      `Apply auto-heal to "${collection}"? This action is irreversible.`
    )) return;
    setHealing(collection);
    try {
      const r = await axiosInstance.post(`/data-integrity/heal/${collection}?dry_run=${dryRun}`);
      toast.success(
        dryRun
          ? `محاكاة: ${r.data.repaired} سيتم إصلاحه / Dry-run: ${r.data.repaired} would be repaired`
          : `تم إصلاح ${r.data.repaired} سجل / Repaired ${r.data.repaired} rows`
      );
      // Refresh issues list
      const issuesResp = await axiosInstance.get(`/data-integrity/issues?collection=${collection}&limit=50`);
      setIssues(issuesResp.data);
      if (!dryRun) await handleScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setHealing(null);
    }
  };

  const openCollectionIssues = async (coll) => {
    setSelectedCollection(coll);
    try {
      const r = await axiosInstance.get(`/data-integrity/issues?collection=${coll}&limit=50`);
      setIssues(r.data);
    } catch (e) {
      setIssues([]);
    }
  };

  const overallHealth = scan
    ? (scan.total_issues === 0 ? 100 :
        Math.round((scan.per_collection.reduce((s, c) => s + (c.total_rows - c.bad_rows), 0) /
                    Math.max(scan.per_collection.reduce((s, c) => s + c.total_rows, 0), 1)) * 100))
    : null;

  const healthColor = overallHealth === null ? 'slate' :
    overallHealth >= 99 ? 'emerald' :
    overallHealth >= 90 ? 'blue' :
    overallHealth >= 70 ? 'amber' : 'rose';

  return (
    <div className="space-y-4" data-testid="data-integrity-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck size={24} className="text-emerald-600" />{t('dataintegrity.data_integrity_framework')}</h1>
        {scan?.finished_at && (
          <span className="text-xs text-gray-500">
            آخر فحص / Last scan: {new Date(scan.finished_at).toLocaleString('en')}
          </span>
        )}
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 rounded-2xl shadow-xl p-6 border border-emerald-900/30">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <p className="text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-2">P7 Engine</p>
            <h2 className="text-2xl font-bold text-white mb-2">
              يحمي النظام من فساد البيانات
            </h2>
            <p className="text-slate-300 text-sm">
              يفحص كل المجموعات الحرجة (Journals, Payments, Invoices, Bank, Accounts, Projects…) ضد نماذج Pydantic
              ويكشف أي حقول مفقودة أو أنواع غير صحيحة. يطبّق إصلاحات آمنة تلقائياً للحالات القابلة للحل.
            </p>
            <div className="flex flex-wrap items-end gap-3 mt-5">
              <button
                onClick={handleScan}
                disabled={running || !isAdmin}
                data-testid="run-scan-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold disabled:opacity-50 hover:opacity-90 shadow-lg shadow-emerald-900/40 transition"
              >
                {running ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                تشغيل الفحص / Run Full Scan
              </button>
              <button
                onClick={fetchAll}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 text-slate-200 hover:bg-slate-600 transition"
                data-testid="refresh-btn"
              >
                <RefreshCw size={14} />{t('dataintegrity.refresh')}</button>
            </div>
            {!isAdmin && (
              <p className="text-xs text-amber-300 mt-2">يلزم صلاحية Admin لتشغيل الفحص.</p>
            )}
          </div>

          {/* Health Score */}
          <div className={`rounded-xl bg-gradient-to-br border p-5 text-white ${
            healthColor === 'emerald' ? 'from-emerald-500 to-emerald-700 border-emerald-300' :
            healthColor === 'blue'    ? 'from-blue-500 to-indigo-600 border-blue-300' :
            healthColor === 'amber'   ? 'from-amber-500 to-orange-600 border-amber-300' :
            healthColor === 'rose'    ? 'from-rose-500 to-rose-700 border-rose-300' :
            'from-slate-600 to-slate-800 border-slate-400'
          }`}>
            <p className="text-xs uppercase tracking-wider font-semibold opacity-80 mb-2">Data Health Score</p>
            <p className="text-6xl font-bold leading-none" data-testid="health-score">
              {overallHealth ?? '—'}
              {overallHealth !== null && <span className="text-2xl opacity-70">%</span>}
            </p>
            <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white/80 transition-all"
                   style={{ width: `${overallHealth ?? 0}%` }} />
            </div>
            <p className="text-xs mt-3 opacity-90" data-testid="open-issues-count">
              {dashboard?.open_issues ?? 0} مشكلة مفتوحة / open issues
            </p>
          </div>
        </div>
      </div>

      {/* Collections health table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-base text-gray-800 flex items-center gap-2">
            <Database size={18} className="text-emerald-600" />{t('dataintegrity.collections_health')}</h2>
          {scan && (
            <span className="text-xs text-gray-500">
              {scan.total_collections} collections · {scan.total_issues} issues · {scan.total_healable} healable
            </span>
          )}
        </div>
        {!scan ? (
          <div className="py-12 text-center text-gray-400">
            <p>{t('dataintegrity.no_scan_yet_click_run_full_scan')}</p>
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs">
              <tr>
                <th className="px-4 py-2 text-left text-gray-600 uppercase">Collection</th>
                <th className="px-4 py-2 text-right text-gray-600 uppercase">Rows</th>
                <th className="px-4 py-2 text-right text-gray-600 uppercase">Issues</th>
                <th className="px-4 py-2 text-right text-gray-600 uppercase">Healable</th>
                <th className="px-4 py-2 text-center text-gray-600 uppercase">Health</th>
                <th className="px-4 py-2 text-center text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {scan.per_collection.map(c => (
                <tr key={c.collection} className="hover:bg-emerald-50/30">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{c.label}</div>
                    <div className="text-xs text-gray-500 font-mono">{c.collection}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{c.total_rows}</td>
                  <td className={`px-4 py-3 text-right font-mono font-bold ${c.bad_rows > 0 ? 'text-rose-600' : 'text-gray-400'}`}>
                    {c.bad_rows}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {c.healable > 0 ? (
                      <span className="text-emerald-600 font-semibold">{c.healable}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-24 h-2 bg-gray-100 rounded overflow-hidden">
                        <div className={`h-full ${
                          c.health_pct === 100 ? 'bg-emerald-500' :
                          c.health_pct >= 90 ? 'bg-blue-500' :
                          c.health_pct >= 70 ? 'bg-amber-500' : 'bg-rose-500'
                        }`} style={{ width: `${c.health_pct}%` }} />
                      </div>
                      <span className="text-xs font-mono">{c.health_pct}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {c.bad_rows > 0 && (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openCollectionIssues(c.collection)}
                          className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                          data-testid={`view-${c.collection}`}
                        >
                          <Eye size={12} className="inline" /> عرض
                        </button>
                        {c.healable > 0 && isAdmin && (
                          <>
                            <button
                              onClick={() => handleHeal(c.collection, true)}
                              disabled={healing === c.collection}
                              className="px-2 py-1 text-xs rounded bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              data-testid={`dry-heal-${c.collection}`}
                            >
                              Dry
                            </button>
                            <button
                              onClick={() => handleHeal(c.collection, false)}
                              disabled={healing === c.collection}
                              className="px-2 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                              data-testid={`heal-${c.collection}`}
                            >
                              {healing === c.collection ? <Loader2 size={10} className="animate-spin inline" /> : <Wrench size={10} className="inline" />} Heal
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Scan history */}
      {runs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="font-bold text-base mb-3 flex items-center gap-2">
            <History size={18} className="text-amber-600" />{t('dataintegrity.scan_history')}</h2>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {runs.map(r => (
              <div key={r.id} className="flex items-center justify-between p-2 text-sm border border-gray-100 rounded-lg">
                <span className="font-mono text-xs">{new Date(r.started_at).toLocaleString('en')}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${r.total_issues === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {r.total_issues} issues
                  </span>
                  <span className="text-xs text-gray-500">{r.total_collections} colls</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues modal */}
      {selectedCollection && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
              <h2 className="font-bold">المشاكل في {selectedCollection} / Issues in {selectedCollection}</h2>
              <button onClick={() => setSelectedCollection(null)} className="p-1 rounded hover:bg-white/10">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {issues.length === 0 ? (
                <p className="text-center text-emerald-600 py-12">
                  <CheckCircle size={32} className="mx-auto mb-2" />{t('dataintegrity.no_issues')}</p>
              ) : (
                issues.map(i => (
                  <div key={i.id} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-mono text-xs text-gray-600">{i.doc_id?.slice(0, 12) || '—'}</div>
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                        i.healable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                      }`}>{i.healable ? t('dataintegrity.healable') : t('dataintegrity.manual')}</span>
                    </div>
                    {i.doc_ref && <div className="text-sm font-semibold mb-1">{i.doc_ref}</div>}
                    <ul className="space-y-1 text-xs">
                      {i.issues.map((iss, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <AlertTriangle size={12} className="text-amber-500 mt-0.5 shrink-0" />
                          <span>
                            <strong>{iss.loc}</strong>: {iss.msg}
                            <span className="text-gray-400"> ({iss.type})</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataIntegrity;
